import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useCreateTemplate } from "../../../../queries/dynamicBonuses/useCreateTemplate";
import { useGetTemplateClone } from "../../../../queries/dynamicBonuses/useGetTemplateClone";
import CreateCampaignWizard from "../Campaigns/CreateCampaignWizard/CreateCampaignWizard";
import { templateCloneToWizardValues } from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import type { TemplateIn } from "../types";

/**
 * Template creation reuses the campaign wizard in "template" mode (the campaign
 * wizard is the source of truth for step/eligibility/cap authoring). The wizard
 * assembles the `TemplateIn`; this page only owns the create mutation and the
 * post-success navigation back to the list. Supersedes the former single-page
 * accordion form.
 */
const CreateTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneFrom = searchParams.get("cloneFrom")?.trim() ?? "";
  const { mutate: createTemplate, isPending } = useCreateTemplate();
  const {
    data: clone,
    isLoading: cloneLoading,
    isError: cloneError,
  } = useGetTemplateClone(cloneFrom);

  const initialValues = useMemo(
    () => (clone ? templateCloneToWizardValues(clone) : undefined),
    [clone],
  );

  const handleSubmit = useCallback(
    (template: TemplateIn) => {
      createTemplate(template, {
        onSuccess: () => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES),
      });
    },
    [createTemplate, navigate],
  );

  // `title` / `submitLabel` are left to the wizard, which already defaults both
  // to "Create Template" in template mode — avoids a second source of truth.
  return (
    <CreateCampaignWizard
      mode="template"
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      initialValues={initialValues}
      isInitialValuesLoading={Boolean(cloneFrom) && cloneLoading}
      initialValuesError={
        cloneFrom && cloneError
          ? AppStrings.PAM_DYNAMIC_BONUS_CLONE_TEMPLATE_LOAD_ERROR
          : undefined
      }
    />
  );
};

export default CreateTemplatePage;
